import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import mixpanel from 'mixpanel-browser';
import { APP_STORE_URL } from '../constants';

// Purple color palette for landing page
const purpleColors = {
  50: '#faf5ff',
  100: '#f3e8ff',
  200: '#e9d5ff',
  300: '#d8b4fe',
  400: '#c084fc',
  500: '#a855f7',
  600: '#9333ea',
  700: '#7e22ce',
};

const slateColors = {
  50: '#f8fafc',
  100: '#f1f5f9',
  200: '#e2e8f0',
  300: '#cbd5e1',
  400: '#94a3b8',
  600: '#475569',
  700: '#334155',
  900: '#0f172a',
};

const greenColors = {
  100: '#dcfce7',
  400: '#4ade80',
  500: '#22c55e',
  700: '#15803d',
};

type HeroProps = {};

const getLocalizedLiveActivityScreenshot = (language: string): string => {
  const base = '/images/Applandingpage/Live%20Activity';
  const normalized = language.toLowerCase();
  if (normalized.startsWith('de')) {
    return `${base}/LiveActivity_DE.jpeg`;
  }
  if (normalized.startsWith('fr')) {
    return `${base}/LiveActivity_FR.jpeg`;
  }
  if (normalized.startsWith('nl')) {
    return `${base}/LiveActivity_NL.jpeg`;
  }
  return `${base}/LiveActivity_EN.jpeg`;
};

const ping = keyframes`
  75%, 100% {
    transform: scale(2);
    opacity: 0;
  }
`;

const HeroSection = styled.section`
  position: relative;
  overflow: hidden;
  background: linear-gradient(to bottom right, ${slateColors[50]}, white, ${purpleColors[50]});
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: 
      linear-gradient(to right, ${purpleColors[500]}0a 1px, transparent 1px),
      linear-gradient(to bottom, ${purpleColors[500]}0a 1px, transparent 1px);
    background-size: 24px 24px;
  }
`;

const HeroImageWrapper = styled.div`
  order: -1;
  margin-bottom: 2rem;
  position: relative;
  
  @media (min-width: 1024px) {
    order: 0;
    margin-bottom: 0;
    align-self: flex-start;
    margin-left: 5rem;
  }
`;


const HeroContent = styled.div`
  position: relative;
  z-index: 10;
  max-width: 80rem;
  margin: 0 auto;
  padding: 3rem 1.5rem 4rem;
  
  @media (min-width: 1024px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 3rem;
    align-items: center;
    padding: 3rem 3rem 4rem;
  }
`;

const HeroText = styled.div`
  text-align: center;
  
  @media (min-width: 1024px) {
    text-align: left;
  }
`;


const BetaBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: ${greenColors[100]};
  color: ${greenColors[700]};
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 1rem;
`;

const PingDot = styled.span`
  position: relative;
  display: flex;
  height: 0.5rem;
  width: 0.5rem;
  
  &::before {
    content: '';
    position: absolute;
    animation: ${ping} 1s cubic-bezier(0, 0, 0.2, 1) infinite;
    border-radius: 9999px;
    background: ${greenColors[400]};
    opacity: 0.75;
    height: 100%;
    width: 100%;
  }
  
  &::after {
    content: '';
    position: relative;
    border-radius: 9999px;
    background: ${greenColors[500]};
    height: 100%;
    width: 100%;
  }
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  color: ${slateColors[900]};
  margin-bottom: 1rem;
  line-height: 1.1;
  
  @media (min-width: 768px) {
    font-size: 3.5rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 3.75rem;
  }
`;

const TitleHighlight = styled.span`
  background: linear-gradient(to right, ${purpleColors[600]}, ${purpleColors[500]});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Description = styled.p`
  font-size: 1.125rem;
  color: ${slateColors[600]};
  margin-bottom: 1.5rem;
  max-width: 48rem;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  
  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 1.125rem;
    margin-left: 0;
    margin-right: 0;
  }
`;

const CTAButtons = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  
  @media (min-width: 640px) {
    flex-direction: row;
    justify-content: center;
  }
  
  @media (min-width: 1024px) {
    justify-content: flex-start;
  }
`;

const PrimaryCTA = styled.a`
  padding: 0.875rem 1.75rem;
  background: ${purpleColors[600]};
  color: white;
  border-radius: 9999px;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.2s;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  text-decoration: none;
  
  &:hover {
    background: ${purpleColors[700]};
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    
    svg {
      transform: translateX(0.25rem);
    }
  }
  
  svg {
    width: 1.125rem;
    height: 1.125rem;
    transition: transform 0.2s;
  }
`;

const SecondaryCTA = styled.a`
  padding: 0.875rem 1.75rem;
  background: white;
  color: ${slateColors[900]};
  border-radius: 9999px;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.2s;
  box-shadow: inset 0 0 0 2px ${slateColors[200]};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  
  &:hover {
    background: ${slateColors[50]};
    box-shadow: inset 0 0 0 2px ${slateColors[300]};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  max-width: 32rem;
  margin: 0 auto;
  padding-top: 1.5rem;
  border-top: 1px solid ${slateColors[200]};
  
  @media (min-width: 1024px) {
    margin: 0;
    max-width: 100%;
  }
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${slateColors[900]};
  margin-bottom: 0.25rem;
  
  @media (min-width: 1024px) {
    font-size: 1.75rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: ${slateColors[600]};
`;

const GradientBottom = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 8rem;
  background: linear-gradient(to top, white, transparent);
`;

const HeroImageContainer = styled.div`
  max-width: 12rem;
  margin: 0 auto;
  position: relative;
  
  @media (min-width: 768px) {
    max-width: 14rem;
  }
  
  @media (min-width: 1024px) {
    max-width: 24rem;
    margin: 0;
    width: 100%;
    transform: translateY(0);
    margin-bottom: -25%;
  }
`;

const PhoneFrame = styled.div`
  position: relative;
  background: ${slateColors[900]};
  border-radius: 1.9rem;
  padding: 0.375rem;
  box-shadow: 
    0 25px 50px -12px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(0, 0, 0, 0.1),
    inset 0 0 0 1px rgba(255, 255, 255, 0.1);
  
  @media (min-width: 768px) {
    border-radius: 2.25rem;
    padding: 0.5rem;
  }
  
  @media (min-width: 1024px) {
    border-radius: 2.5rem;
    padding: 0.5rem;
  }
`;

const ScreenshotImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  border-radius: 1.5rem;
  background: white;
  
  @media (min-width: 768px) {
    border-radius: 1.75rem;
  }
  
  @media (min-width: 1024px) {
    border-radius: 2rem;
  }
`;

export default function Hero({}: HeroProps) {
  const { t, i18n } = useTranslation();
  const heroScreenshotSrc = getLocalizedLiveActivityScreenshot(i18n.language);

  return (
    <HeroSection>
      <HeroContent>
        <HeroImageWrapper>
          <HeroImageContainer>
            <PhoneFrame>
            <ScreenshotImage
              src={heroScreenshotSrc}
              alt={t('appLandingPage.hero.screenshotAlt')}
            />
            </PhoneFrame>
          </HeroImageContainer>
        </HeroImageWrapper>

        <HeroText>
        <BetaBadge>
          <PingDot />
          {t('appLandingPage.hero.betaBadge')}
        </BetaBadge>

        <Title>
          {t('appLandingPage.hero.titleLine1')}
          <br />
          <TitleHighlight>{t('appLandingPage.hero.titleHighlight')}</TitleHighlight>
        </Title>

        <Description>{t('appLandingPage.hero.description')}</Description>

        <CTAButtons>
          <PrimaryCTA
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              mixpanel.track('Download on App Store link clicked', {
                page: window.location.pathname,
              });
            }}
          >
            {t('appLandingPage.hero.primaryCta')}
            <ArrowRight />
          </PrimaryCTA>
          <SecondaryCTA href="#features">
            {t('appLandingPage.hero.secondaryCta')}
          </SecondaryCTA>
        </CTAButtons>

        <StatsGrid>
          <div>
            <StatValue>10</StatValue>
            <StatLabel>{t('appLandingPage.hero.stats.supportedCountries')}</StatLabel>
          </div>
          <div>
            <StatValue>160K+</StatValue>
            <StatLabel>{t('appLandingPage.hero.stats.routesTracked')}</StatLabel>
          </div>
          <div>
            <StatValue>{t('common.realtime')}</StatValue>
            <StatLabel>{t('appLandingPage.hero.stats.liveUpdates')}</StatLabel>
            </div>
        </StatsGrid>
        </HeroText>
      </HeroContent>

      <GradientBottom />
    </HeroSection>
  );
}

